<script setup>
/* eslint-disable no-undef */
import { defineProps, defineEmits } from 'vue';
import axios from 'axios';
import { ref, onMounted, computed } from 'vue';
import config from "@/utils/config";

const questions = ref([]);
const emit = defineEmits(["questionnaire-completed"]);

const props = defineProps({
  user: {
    type: Object,
    required: true
  },
  description: {
    type: String,
    default: ''
  },
  options: {
    type: Array,
    default: () => ["Fortemente in disaccordo", "In disaccordo", "Neutro", "D'accordo", "Fortemente d'accordo"],
  },
  tlx: {
    type: Boolean,
    default: false
  }
}); 

onMounted(() => {
  questions.value = [
    { id: 1, text: "1. Penso che mi piacerebbe utilizzare questo sistema frequentemente", answer: null },
    { id: 2, text: "2. Ho trovato il sistema complesso senza che ce ne fosse bisogno", answer: null },
    { id: 3, text: "3. Ho trovato il sistema molto semplice da usare", answer: null },
    { id: 4, text: "4. Penso che avrei bisogno del supporto di una persona già in grado di utilizzare il sistema", answer: null },
    { id: 5, text: "5. Ho trovato le varie funzionalità del sistema bene integrate", answer: null },
    { id: 6, text: "6. Ho trovato incoerenze tra le varie funzionalità del sistema", answer: null },
    { id: 7, text: "7. Penso che la maggior parte delle persone potrebbero imparare ad utilizzare il sistema facilmente", answer: null },
    { id: 8, text: "8. Ho trovato il sistema molto macchinoso da utilizzare", answer: null },
    { id: 9, text: "9. Ho avuto molta confidenza con il sistema durante l’uso", answer: null },
    { id: 10, text: "10. Ho avuto bisogno di imparare molti processi prima di riuscire ad utilizzare al meglio il sistema", answer: null },
  ];
});

function updateAnswer(questionId, value) {
  const question = questions.value.find(q => q.id === questionId);
  if (question) {
    question.answer = value;
  }
}

const allQuestionsAnswered = computed(() => {
  return questions.value.every(question => question.answer !== null);
});

function answeredQuestions() {
    const payload = {
        frequenzaUso: responseMapping[questions.value[0].answer],
        confusioneDuranteUso: responseMapping[questions.value[1].answer],
        facilitaUso: responseMapping[questions.value[2].answer],
        bisognoAiuto: responseMapping[questions.value[3].answer],
        chiarezzaAzioni: responseMapping[questions.value[4].answer], 
        sensoDelleAzioni: responseMapping[questions.value[5].answer],
        facilitaApprendimentoAmici: responseMapping[questions.value[6].answer],
        stranezzaAzioni: responseMapping[questions.value[7].answer],
        sicurezzaDuranteUso: responseMapping[questions.value[8].answer],
        apprendimentoPreliminare: responseMapping[questions.value[9].answer],
        user: { id: props.user.id }
    };
      
    axios.post(config.baseUrl + `/finalQuestionnaire/addScores`, payload)
        .then(() => {
          emit("questionnaire-completed");
        })
        .catch(error => {
        console.error('Error posting questionnaire:', error);
        });
}

const responseMapping = {
  "Fortemente in disaccordo": 1,
  "In disaccordo": 2,
  "Neutro": 3,
  "D'accordo": 4,
  "Fortemente in accordo": 5,
};
</script>

<template>
  <div class="questionnaire">
    <h3 style="margin-bottom: 20px" v-html="description"></h3>

    <!-- TLX Layout -->
    <div v-if="tlx" class="tlx-container">
      <div v-for="question in questions" :key="question.id" class="tlx-question">
        <div class="tlx-text">
          <div class="question-text">{{ question.text }}</div>
        </div>
        <div class="tlx-scale">
          <div class="scale-labels">
            <span>Very Low</span>
            <span>Very High</span>
          </div>
          <div class="scale-container" 
               @click="(e) => {
                 const rect = e.currentTarget.getBoundingClientRect();
                 const x = e.clientX - rect.left;
                 const boxIndex = Math.min(Math.floor(x / (rect.width / 21)), 20);
                 updateAnswer(question.id, { percentage: (boxIndex / 20) * 100, boxIndex });
               }">
            <div class="scale-boxes">
              <div v-for="n in 21" :key="n"
                   class="box"
                   :class="{ selected: question.answer?.boxIndex === (n-1) }">
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <table v-else>
      <thead>
        <tr>
          <th>Question</th>
          <th v-for="option in options" :key="option">{{ option }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="question in questions" :key="question.id">
          <td class="question-td" v-html="question.text"></td>
          <td v-for="option in options" :key="option" @click="updateAnswer(question.id, option)">
            <input
                type="radio"
                :name="`question-${question.id}`"
                :value="option"
                :checked="question.answer === option"
            />
          </td>
        </tr>
      </tbody>
    </table>

    <button class="btn-primary"
        :disabled="!allQuestionsAnswered"
        @click="answeredQuestions"
        :title="!allQuestionsAnswered ? 'Please answer all questions before continuing': ''">Continue
    </button>
  </div>
</template>

<style scoped>
table {
  width: 100%;
  border-collapse: collapse;
}

th, td {
  border: 1px solid #ccc;
  padding: 8px;
  text-align: center;
}

.question-td {
  text-align: left;
}

th {
  background-color: transparent;
  color: white;
}

td {
  cursor: pointer;
}

.tlx-container {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
}

.tlx-question {
  margin-bottom: 2rem;
}

.tlx-text {
  margin-bottom: 0.5rem;
}

.scale-labels {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.25rem;
  font-size: 0.9em;
  color: #666;
}

.scale-container {
  position: relative;
  height: 30px;
  cursor: pointer;
}

.scale-boxes {
  display: flex;
  height: 100%;
  gap: 1px;
}

.box {
  flex: 1;
  border: 1px solid #ccc;
  background: #f9f9f9;
  transition: background-color 0.2s ease;
}

.box.selected {
  background-color: #007bff;
  border-color: #0056b3;
}

button {
  margin-top: 20px;
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  background-color: #007bff;
  color: white;
  cursor: pointer;
}

button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}
</style>
